import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';

// Points for a five-pointed star centred on (cx, cy), first point aimed at `angle` (degrees, 0 = up)
const starPoints = (cx, cy, r, angle = 0) => {
  const inner = r * 0.382;
  return Array.from({ length: 10 }, (_, i) => {
    const radius = i % 2 === 0 ? r : inner;
    const theta = ((angle + i * 36) * Math.PI) / 180;
    return `${(cx + radius * Math.sin(theta)).toFixed(3)},${(cy - radius * Math.cos(theta)).toFixed(3)}`;
  }).join(' ');
};

// Angle (degrees, 0 = up) from a small star towards the large star at (5, 5)
const towardsMainStar = (x, y) => (Math.atan2(5 - x, -(5 - y)) * 180) / Math.PI;

function FlagUK() {
  const id = useId().replace(/:/g, '');
  return (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true" className="h-full w-full">
      <clipPath id={`uk-s-${id}`}>
        <path d="M0,0 v30 h60 v-30 z" />
      </clipPath>
      <clipPath id={`uk-t-${id}`}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <g clipPath={`url(#uk-s-${id})`}>
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#uk-t-${id})`} stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

function FlagCN() {
  const smallStars = [
    [10, 2],
    [12, 4],
    [12, 7],
    [10, 9],
  ];
  return (
    <svg viewBox="0 0 30 20" preserveAspectRatio="xMidYMid slice" aria-hidden="true" className="h-full w-full">
      <rect width="30" height="20" fill="#EE1C25" />
      <polygon points={starPoints(5, 5, 3)} fill="#FFFF00" />
      {smallStars.map(([x, y]) => (
        <polygon key={`${x}-${y}`} points={starPoints(x, y, 1, towardsMainStar(x, y))} fill="#FFFF00" />
      ))}
    </svg>
  );
}

function FlagVN() {
  return (
    <svg viewBox="0 0 30 20" preserveAspectRatio="xMidYMid slice" aria-hidden="true" className="h-full w-full">
      <rect width="30" height="20" fill="#DA251D" />
      <polygon points={starPoints(15, 10, 6)} fill="#FFFF00" />
    </svg>
  );
}

const LANGUAGES = [
  { code: 'en', short: 'EN', nativeName: 'English', englishName: 'English', Flag: FlagUK },
  { code: 'zh', short: '中文', nativeName: '简体中文', englishName: 'Chinese (Mandarin)', Flag: FlagCN },
  { code: 'vi', short: 'VI', nativeName: 'Tiếng Việt', englishName: 'Vietnamese', Flag: FlagVN },
];

function FlagBadge({ Flag, className }) {
  return (
    <span
      className={cn(
        'relative inline-flex h-3.5 w-5 shrink-0 overflow-hidden rounded-[3px] ring-1 ring-white/20 shadow-sm',
        className
      )}
    >
      <Flag />
    </span>
  );
}

export default function LanguageSwitcher({ className, compact = false }) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const listboxId = `language-listbox-${useId().replace(/:/g, '')}`;

  const currentIndex = Math.max(0, LANGUAGES.findIndex((lang) => lang.code === language));
  const current = LANGUAGES[currentIndex];

  const closeMenu = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback((index = currentIndex) => {
    setActiveIndex(index);
    setOpen(true);
  }, [currentIndex]);

  const selectLanguage = useCallback((code) => {
    setLanguage(code);
    closeMenu(true);
  }, [setLanguage, closeMenu]);

  // Close when clicking or tapping outside
  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
    };
  }, [open, closeMenu]);

  // Move focus to the highlighted option while the menu is open
  useEffect(() => {
    if (open) optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openMenu(currentIndex);
    }
  };

  const handleMenuKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % LANGUAGES.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + LANGUAGES.length) % LANGUAGES.length);
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(LANGUAGES.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu(true);
        break;
      case 'Tab':
        closeMenu();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 py-1.5 pl-2 pr-2 text-xs font-medium text-white/80 shadow-inner backdrop-blur-xl transition-colors duration-200 select-none',
          'hover:border-white/20 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
          open && 'border-white/20 bg-white/10 text-white'
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={`Language: ${current.nativeName}`}
        title={current.englishName}
      >
        <FlagBadge Flag={current.Flag} />
        {!compact && <span className="tracking-wider">{current.short}</span>}
        <ChevronDown
          className={cn('h-3.5 w-3.5 text-white/50 transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            id={listboxId}
            role="listbox"
            aria-label="Select language"
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onKeyDown={handleMenuKeyDown}
            className="absolute right-0 top-full z-[60] mt-2 w-52 origin-top-right overflow-hidden rounded-2xl border border-white/10 bg-[#0B0B0F]/95 p-1.5 shadow-2xl shadow-black/60 backdrop-blur-xl"
          >
            {LANGUAGES.map((lang, index) => {
              const isSelected = lang.code === language;
              const isActive = index === activeIndex;
              return (
                <li key={lang.code} role="presentation">
                  <button
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    id={`${listboxId}-${lang.code}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    lang={lang.code}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectLanguage(lang.code)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors duration-150 focus:outline-none',
                      isActive ? 'bg-white/10' : 'bg-transparent',
                      isSelected ? 'text-white' : 'text-white/70'
                    )}
                  >
                    <FlagBadge Flag={lang.Flag} className="h-4 w-6" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className={cn('text-sm leading-tight', isSelected ? 'font-semibold' : 'font-medium')}>
                        {lang.nativeName}
                      </span>
                      <span className="text-[11px] leading-tight text-white/40" lang="en">
                        {lang.englishName}
                      </span>
                    </span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-white/80" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
